// Cross-tab "this recording is live RIGHT NOW" signal, written by useRecorder on
// every segment flush. Read by useIndexDb (a PARENT provider that cannot import
// useRecorder) to BLOCK a finalless rebuild of a still-recording set. BLOCK-ONLY:
// a fresh heartbeat can stop a rebuild, but its ABSENCE never authorizes one — the
// ~120s segment-recency gate is the load-bearing authority (spec §14A). Map of
// {recordingId: ts}, viewer-scoped, recency-capped (mirrors recordingRecoveryDismissState
// MAX_IDS + peptideRestockAckState's map shape).
import { recordingViewerKey } from "./recordingViewerKey";

const HB_PREFIX = "recordingActiveHeartbeat:";
const MAX_IDS = 50;

// Session-scoped (in-memory) marker of the recording THIS tab is actively capturing.
// Deliberately NOT localStorage: a persisted marker SURVIVES a tab crash/close, which
// makes the NEXT session treat a CRASHED recording as "still recording" and refuse to
// recover it forever — the exact bug Path A exists to fix (found in stage smoke). A
// module variable resets on page load, so a fresh session never inherits a dead
// session's marker, while the SAME session still protects its live/paused capture.
// Cross-tab + crash liveness is the heartbeat's job (freshness-based localStorage).
let activeRecordingId = null;

function hbKey() {
  return HB_PREFIX + recordingViewerKey();
}

function readMap() {
  try {
    const raw = localStorage.getItem(hbKey());
    if (!raw) return {};
    const p = JSON.parse(raw);
    return p && typeof p === "object" ? p : {};
  } catch {
    return {};
  }
}
function writeMap(map) {
  try {
    const keys = Object.keys(map);
    if (keys.length > MAX_IDS) {
      // Drop the oldest by timestamp.
      keys.sort((a, b) => (map[a] || 0) - (map[b] || 0));
      for (let i = 0; i < keys.length - MAX_IDS; i++) delete map[keys[i]];
    }
    localStorage.setItem(hbKey(), JSON.stringify(map));
  } catch {
    // Quota / private-mode failures are silent — heartbeat is best-effort.
  }
}

export function writeHeartbeat(recordingId) {
  if (!recordingId) return;
  const map = readMap();
  map[recordingId] = Date.now();
  writeMap(map);
}
export function clearHeartbeat(recordingId) {
  if (!recordingId) return;
  const map = readMap();
  if (map[recordingId] != null) {
    delete map[recordingId];
    writeMap(map);
  }
}
export function isHeartbeatFresh(recordingId, windowMs) {
  if (!recordingId) return false;
  const ts = readMap()[recordingId] || 0;
  return Date.now() - ts < windowMs;
}
// Every recordingId whose heartbeat is younger than `windowMs`, from ONE read of the
// map. The recordings list used to ask isHeartbeatFresh per ROW, which re-read and
// re-parsed the same localStorage entry once per queued segment — thousands of times
// on the queue this exists for. Same block-only semantics as isHeartbeatFresh: a
// listed id is one some tab is still writing to; an absent one proves nothing.
export function freshHeartbeatIds(windowMs) {
  const fresh = new Set();
  if (!(windowMs > 0)) return fresh;
  const map = readMap();
  const now = Date.now();
  for (const id of Object.keys(map)) {
    const ts = map[id];
    if (typeof ts === "number" && now - ts < windowMs) fresh.add(id);
  }
  return fresh;
}
export function setActiveRecording(recordingId) {
  activeRecordingId = recordingId || null;
}
export function clearActiveRecording(recordingId) {
  // Clear unconditionally when no id is given, else only if it still points at this id
  // (so a newer capture's marker isn't wiped by a late finalize of an older one).
  if (!recordingId || activeRecordingId === recordingId) activeRecordingId = null;
}
export function getActiveRecording() {
  return activeRecordingId;
}
